import type { ExtractionCitation } from '@ctw/shared/api/ai/use-extraction-jobs';
import { getProvenanceTargetIds, getSourceBinaryId } from '@ctw/shared/api/fhir/binaries';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import { searchProvenancesFQS } from '@ctw/shared/api/fqs/queries/provenances';
import { resourceDrawerActionForModel } from '@ctw/shared/context/_internal/resource/resource-drawer';
import {
  type FqsHotstorageRequestOptions,
  fetchResourceByTypeAndIds,
  useFqsHotstorageRequestOptions,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import {
  type UsePatientQueryFnContext,
  usePatientQuery,
} from '@ctw/shared/context/patient-provider';
import type { CustomQueryFnContext } from '@ctw/shared/hooks/use-ctw-query';
import { isFetchableResourceType } from '@ctw/shared/utils/fhir/resource-mappings';
import { retryWithExponentialBackoff } from '@ctw/shared/utils/request';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import type { QueryClient } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';
import type { Provenance, Resource } from 'fhir/r4';
import { groupBy, uniq } from 'lodash-es';

type Context = UsePatientQueryFnContext<CustomQueryFnContext>;

/**
 * How a citation can be surfaced to the user:
 * - `document`: traceable to a source document, opens in the document viewer.
 * - `drawer`: no source document, but the resource type renders in the resource drawer.
 * Citations that resolve to neither are omitted (their document icon is greyed out).
 */
export type CitationSource =
  | { kind: 'document'; binaryId: string; label: string }
  | { kind: 'drawer'; model: FHIRModel<Resource>; label: string };

// Stable identity for the no-citations case so consumers' memos don't rebuild
// every render while the query is disabled or loading.
const EMPTY_SOURCE_MAP: ReadonlyMap<number, CitationSource> = new Map();

// Resolution fans out into several FQS calls at once and is prone to transient
// 429s and dropped connections. Each group retries with backoff (1s, then 2s)
// before its failure is allowed to surface.
const RESOLVE_RETRIES = 2;
const RESOLVE_RETRY_BASE_DELAY_MS = 500;

function toCitationSource(
  model: FHIRModel<Resource>,
  binaryId: string | undefined,
): CitationSource | undefined {
  // If we have a binary, we can open in the document viewer
  if (binaryId) {
    return { kind: 'document', binaryId, label: model.title };
  }
  // Otherwise, if the drawer supports it we'll open in a drawer
  if (resourceDrawerActionForModel(model as never)) {
    return { kind: 'drawer', model, label: model.title };
  }
  return undefined;
}

// FQS answers `Type(id:)` for a target it can't retrieve with a "resource not
// found" error, and when every target in a batch is missing the whole request
// fails with no data. A missing target has no provenance to offer, so an
// all-not-found response is an empty result, not a lookup that MUST be retried.
function isAllResourceNotFound(error: unknown): boolean {
  return (
    error instanceof AggregateError &&
    error.errors.length > 0 &&
    error.errors.every((cause) => {
      const message = (cause as { message?: unknown } | undefined)?.message;
      return typeof message === 'string' && message.includes('resource not found');
    })
  );
}

async function searchProvenancesForTargets(
  context: Context,
  resourceType: ResourceTypeString,
  targetIds: Array<string>,
): Promise<Array<Provenance>> {
  try {
    return await searchProvenancesFQS(
      context.telemetry,
      context.graphqlClient,
      resourceType,
      targetIds,
    );
  } catch (error) {
    if (isAllResourceNotFound(error)) {
      return [];
    }
    throw error;
  }
}

// Resolves all citations of one resource type using a single batched resource
// fetch and (for non-document types) a single provenance search. Citations whose
// resource type isn't fetchable resolve to nothing and grey out.
async function resolveCitationGroup(
  resourceType: string,
  citations: Array<ExtractionCitation>,
  context: Context,
  fqsHotstorageRequestOptions: FqsHotstorageRequestOptions,
  queryClient: QueryClient,
): Promise<Array<readonly [number, CitationSource]>> {
  if (!isFetchableResourceType(resourceType)) {
    return [];
  }
  const resourceIds = citations.map((citation) => citation.resourceId) as NonEmptyArray<string>;
  const models = (await fetchResourceByTypeAndIds({
    resourceType,
    resourceIds,
    telemetry: context.telemetry,
    ctwFetch: context.ctwFetch,
    graphqlClient: context.graphqlClient,
    queryClient,
    requestContext: context.requestContext,
    patient: context.patient,
    fqsHotstorageRequestOptions,
  })) as Array<FHIRModel<Resource>>;
  if (models.length === 0) {
    return [];
  }

  const modelById = new Map(models.map((model) => [model.id, model]));
  const binaryByModelId = new Map<string, string>();

  if (resourceType === 'DocumentReference') {
    // DocumentReference models carry their Binary id directly — no provenance lookup needed.
    for (const model of models) {
      if (model instanceof DocumentModel && model.binaryId) {
        binaryByModelId.set(model.id, model.binaryId);
      }
    }
  } else {
    // Summary/lens resources have no provenance of their own; getProvenanceTargetIds
    // follows them to the underlying source resources. Search provenance for the
    // union of targets in one call, then resolve each model's source Binary.
    // Models that carry their source Binary directly (data-acquisition meta
    // extension) are excluded from the provenance search since getSourceBinaryId
    // short-circuits on them — saves the call when every model has the extension.
    const modelsNeedingProvenance = models.filter((model) => !model.sourceBinaryId);
    const targetIds = uniq(modelsNeedingProvenance.flatMap(getProvenanceTargetIds));
    const provenances =
      targetIds.length > 0
        ? await searchProvenancesForTargets(context, resourceType as ResourceTypeString, targetIds)
        : [];
    for (const model of models) {
      const binaryId = getSourceBinaryId(provenances, model);
      if (binaryId) {
        binaryByModelId.set(model.id, binaryId);
      }
    }
  }

  return citations.flatMap((citation) => {
    const model = modelById.get(citation.resourceId);
    if (!model) {
      return [];
    }
    const source = toCitationSource(model, binaryByModelId.get(model.id));
    return source ? [[citation.id, source] as const] : [];
  });
}

/**
 * Eagerly resolves each citation to how it can be surfaced (document viewer,
 * resource drawer, or neither). Citations are batched by resource type so the
 * number of network calls scales with the number of distinct resource types,
 * not the number of citations.
 */
export function useCitationSources(citations: Array<ExtractionCitation>) {
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();
  const queryClient = useQueryClient();

  const query = usePatientQuery<Map<number, CitationSource>>({
    queryId: 'FactExtractionCitationSources',
    queryKey: [citations.map((c) => `${c.id}:${c.resourceType}:${c.resourceId}`).join(',')],
    queryFn: async (context) => {
      const citationsByType = groupBy(citations, (citation) => citation.resourceType);
      const groups = await Promise.all(
        Object.entries(citationsByType).map(async ([resourceType, group]) => {
          try {
            return await retryWithExponentialBackoff(
              () =>
                resolveCitationGroup(
                  resourceType,
                  group,
                  context,
                  fqsHotstorageRequestOptions,
                  queryClient,
                ),
              RESOLVE_RETRIES,
              RESOLVE_RETRY_BASE_DELAY_MS,
            );
          } catch (error) {
            context.telemetry.trackError({
              message: 'Failed to resolve fact extraction citation sources',
              error: error as Error,
              context: { resourceType, citationCount: group.length },
            });
            // A failed group MUST fail the whole query. Returning a partial
            // result would be cached as a success, greying out every citation
            // in the group until the cache expires.
            throw error;
          }
        }),
      );
      return new Map(groups.flat());
    },
    enabled: citations.length > 0,
  });

  return {
    sourceByCitationId: query.data ?? EMPTY_SOURCE_MAP,
    isLoading: citations.length > 0 && query.isLoading,
    isError: citations.length > 0 && query.isError,
  };
}
